// src/pages/RoomPage.jsx
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { useSocket } from "../providers/Socket";
import { usePeerManager } from "../providers/PeerManager";
import { useAdmin } from "../admin/AdminContext";
import { useMom } from "../hooks/useMom";

import logo from "../logo.jpg";

import RoomHeader from "../components/RoomHeader";
import MainVideoPanel from "../components/MainVideoPanel";
import SidebarTiles from "../components/SidebarTiles";
import MobileLayout from "../components/MobileLayout";
import MomPanel from "../components/MomPanel";

import "../styles/RoomPage.css";

const RoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const adminCtx = useAdmin();
  const { socket, myEmail } = useSocket();

  const {
    peers,
    remoteStreams,
    setLocalMediaStream,
    createOfferFor,
    createAnswerFor,
    setRemoteAnswerFor,
    addIceCandidateFor,
    removePeer,
    resetAllPeers,
  } = usePeerManager();

  // -------- Admin observer detection --------
  const searchParams = new URLSearchParams(location.search);
  const wantsObserver = searchParams.get("observer") === "1";
  const isAdminObserver = !!(adminCtx && adminCtx.isAdmin && wantsObserver);

  const [myStream, setMyStream] = useState(null);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);

  const [isRemoteAudioOn, setIsRemoteAudioOn] = useState(true);
  const [isRemoteVideoOn, setIsRemoteVideoOn] = useState(true);
  const [isPipHovered, setIsPipHovered] = useState(false);

  const [pinnedId, setPinnedId] = useState(null);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // AI MoM
  const [transcriptDraft, setTranscriptDraft] = useState("");
  const { mom, loading: momLoading, error: momError, generateMom } = useMom();

  // Web Speech API
  const [isAsrAvailable, setIsAsrAvailable] = useState(false);
  const [isAsrOn, setIsAsrOn] = useState(false);
  const recognitionRef = useRef(null);
  const asrShouldRunRef = useRef(false);

  // per-remote media state
  const [remoteMediaState, setRemoteMediaState] = useState({});

  // refs
  const localPipRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteSurfaceRef = useRef(null);
  const streamRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const mediaInitRef = useRef(false);
  const hasJoinedRef = useRef(false);
  const remoteOfferedRef = useRef({});

  // screen share
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenShareStreamRef = useRef(null);
  const cameraVideoTrackRef = useRef(null);

  // PiP drag
  const [pipPos, setPipPos] = useState({ x: 16, y: 16 });
  const dragDataRef = useRef(null);

  // Human label
  const displayName =
    (myEmail && myEmail.trim()) || (isAdminObserver ? "Admin" : "Guest");

  const emailIdForServer = isAdminObserver
    ? `observer:${displayName}`
    : displayName;

  // ====== window resize (mobile/desktop) ======
  useEffect(() => {
    const check = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth < 768);
      }
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ASR vs mic
  useEffect(() => {
    if (!isMicOn && isAsrOn) {
      setIsAsrOn(false);
    }
  }, [isMicOn, isAsrOn]);

  // keep latest stream in ref
  useEffect(() => {
    streamRef.current = myStream;
  }, [myStream]);

  // PiP drag handlers (mobile)
  const onPipDragMove = useCallback((e) => {
    if (!dragDataRef.current || !remoteSurfaceRef.current) return;
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - dragDataRef.current.startX;
    const dy = point.clientY - dragDataRef.current.startY;

    const parentRect = remoteSurfaceRef.current.getBoundingClientRect();
    const pipW = 140;
    const pipH = 90;

    let newX = dragDataRef.current.origX + dx;
    let newY = dragDataRef.current.origY + dy;

    newX = Math.max(0, Math.min(newX, parentRect.width - pipW));
    newY = Math.max(0, Math.min(newY, parentRect.height - pipH));

    setPipPos({ x: newX, y: newY });
  }, []);

  const onPipDragEnd = useCallback(() => {
    dragDataRef.current = null;
    window.removeEventListener("mousemove", onPipDragMove);
    window.removeEventListener("mouseup", onPipDragEnd);
    window.removeEventListener("touchmove", onPipDragMove);
    window.removeEventListener("touchend", onPipDragEnd);
  }, [onPipDragMove]);

  const onPipDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const point = e.touches ? e.touches[0] : e;
      dragDataRef.current = {
        startX: point.clientX,
        startY: point.clientY,
        origX: pipPos.x,
        origY: pipPos.y,
      };
      window.addEventListener("mousemove", onPipDragMove);
      window.addEventListener("mouseup", onPipDragEnd);
      window.addEventListener("touchmove", onPipDragMove, { passive: false });
      window.addEventListener("touchend", onPipDragEnd);
    },
    [pipPos.x, pipPos.y, onPipDragMove, onPipDragEnd]
  );

  // helper: wait for local stream (normal users only)
  const waitForStream = useCallback(() => {
    return new Promise((resolve) => {
      if (streamRef.current) return resolve(streamRef.current);
      const interval = setInterval(() => {
        if (streamRef.current) {
          clearInterval(interval);
          resolve(streamRef.current);
        }
      }, 50);
    });
  }, []);

  // ====== 1. Get local media ======
  useEffect(() => {
    if (isAdminObserver) {
      console.log("[ROOM] Observer mode – skipping local media capture");
      return;
    }

    if (mediaInitRef.current) return;
    mediaInitRef.current = true;

    const initStream = async () => {
      try {
        console.log("[ROOM] Requesting local media...");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        console.log("[ROOM] Got local stream:", stream);

        setMyStream(stream);
        setLocalMediaStream(stream);
      } catch (err) {
        console.error("[ROOM] Error accessing media devices:", err);
      }
    };

    initStream();
  }, [setLocalMediaStream, isAdminObserver]);

  // ====== 2. Join room ======
  useEffect(() => {
    if (!socket || !roomId || !emailIdForServer) return;
    if (!isAdminObserver && !myStream) return;

    if (hasJoinedRef.current) return;
    hasJoinedRef.current = true;

    console.log(
      "[ROOM] Joining room:",
      roomId,
      "as",
      emailIdForServer,
      isAdminObserver ? "(observer)" : ""
    );

    socket.emit("join-room", {
      roomId,
      emailId: emailIdForServer,
      isObserver: isAdminObserver,
    });
  }, [socket, roomId, myStream, emailIdForServer, isAdminObserver]);

  // ====== 2b. Observer snapshot ======
  useEffect(() => {
    if (!socket || !isAdminObserver) return;

    const handleAdminExisting = async ({ roomId: rid, participants }) => {
      if (!participants || !participants.length) return;

      console.log("[ROOM][OBSERVER] existing participants:", participants);

      for (const emailId of participants) {
        if (!emailId) continue;
        if (peers[emailId]) continue;

        try {
          const offer = await createOfferFor(emailId, (to, candidate) => {
            socket.emit("webrtc-candidate", { to, candidate });
          });

          socket.emit("webrtc-offer", { to: emailId, offer });
          console.log(
            "[ROOM][OBSERVER] sent offer to existing participant",
            emailId
          );
        } catch (e) {
          console.warn(
            "[ROOM][OBSERVER] failed to create offer for",
            emailId,
            e
          );
        }
      }
    };

    socket.on("admin-existing-participants", handleAdminExisting);
    return () => {
      socket.off("admin-existing-participants", handleAdminExisting);
    };
  }, [socket, isAdminObserver, peers, createOfferFor]);

  // ====== server forces close ======
  useEffect(() => {
    if (!socket) return;

    const handleForceClose = ({ roomId: closedId, by, reason }) => {
      if (!closedId || closedId !== roomId) return;

      const msg = `This room has been closed by ${
        by || "admin"
      }.\nReason: ${reason || "No reason provided."}`;

      alert(msg);

      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }
      } catch (e) {
        console.warn("[ROOM] Error stopping local tracks on force-close:", e);
      }

      if (typeof resetAllPeers === "function") {
        resetAllPeers();
      }

      navigate("/");
    };

    socket.on("force-close-room", handleForceClose);
    return () => {
      socket.off("force-close-room", handleForceClose);
    };
  }, [socket, roomId, navigate, resetAllPeers]);

  // ====== participants & remotes ======
  const remoteEntries = Object.entries(remoteStreams);
  const connected = remoteEntries.length > 0;

  const participants = [];

  if (myStream && !isAdminObserver) {
    participants.push({
      id: "local",
      isLocal: true,
      email: myEmail || "You",
      stream: myStream,
    });
  }

  remoteEntries.forEach(([emailId, stream]) => {
    participants.push({
      id: emailId,
      isLocal: false,
      email: emailId,
      stream,
    });
  });

  const participantCount = participants.length;

  // ====== active speaker detection ======
  const audioContextRef = useRef(null);
  const analyserMapRef = useRef({});
  const volumeIntervalRef = useRef(null);

  const attachAnalyser = useCallback((id, stream) => {
    if (!stream) return;
    if (analyserMapRef.current[id]) return;

    try {
      const AC = window.AudioContext || window.webkitAudioContext || null;
      if (!AC) return;
      if (!audioContextRef.current) {
        audioContextRef.current = new AC();
      }
      const ctx = audioContextRef.current;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      source.connect(analyser);
      analyserMapRef.current[id] = { analyser, dataArray };
    } catch (e) {
      console.warn("[ROOM] Error attaching analyser for", id, e);
    }
  }, []);

  useEffect(() => {
    if (!myStream && remoteEntries.length === 0) return;

    if (myStream && !isAdminObserver) attachAnalyser("local", myStream);
    remoteEntries.forEach(([emailId, stream]) => {
      attachAnalyser(emailId, stream);
    });

    if (!volumeIntervalRef.current && audioContextRef.current) {
      volumeIntervalRef.current = setInterval(() => {
        const entries = Object.entries(analyserMapRef.current);
        if (!entries.length) return;

        let maxId = null;
        let maxVal = 0;

        entries.forEach(([id, { analyser, dataArray }]) => {
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
          const avg = sum / dataArray.length;
          if (avg > maxVal) {
            maxVal = avg;
            maxId = id;
          }
        });

        const THRESHOLD = 18;
        if (maxVal > THRESHOLD && maxId) {
          setActiveSpeakerId((prev) => (prev === maxId ? prev : maxId));
        }
      }, 150);
    }
  }, [myStream, remoteEntries, attachAnalyser, isAdminObserver]);

  useEffect(() => {
    return () => {
      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
        volumeIntervalRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (_) {}
        audioContextRef.current = null;
      }
      analyserMapRef.current = {};
    };
  }, []);

  // ====== main participant selection ======
  const getMainId = () => {
    const exists = (id) => participants.some((p) => p.id === id && p.stream);

    if (pinnedId && exists(pinnedId)) return pinnedId;

    const firstRemote = participants.find((p) => !p.isLocal && p.stream);
    if (firstRemote) return firstRemote.id;

    if (myStream && !isAdminObserver) return "local";

    return null;
  };

  const mainId = getMainId();
  const mainParticipant =
    participants.find((p) => p.id === mainId) || participants[0] || null;

  const sidebarTiles = mainParticipant
    ? participants.filter((p) => p.id !== mainParticipant.id)
    : [];

  // mobile primary remote
  const primaryRemoteEntry = remoteEntries[0] || null;
  const primaryRemoteEmail = primaryRemoteEntry ? primaryRemoteEntry[0] : null;
  const primaryRemoteStream = primaryRemoteEntry ? primaryRemoteEntry[1] : null;

  useEffect(() => {
    if (remoteVideoRef.current && primaryRemoteStream && isMobile) {
      if (remoteVideoRef.current.srcObject !== primaryRemoteStream) {
        remoteVideoRef.current.srcObject = primaryRemoteStream;
      }
      remoteVideoRef.current
        .play()
        .catch((e) => console.warn("[ROOM] Error playing remote video:", e));
    }
  }, [primaryRemoteStream, isMobile]);

  // ====== signaling handlers ======
  const handleNewUser = useCallback(
    async ({ emailId }) => {
      console.log("[ROOM] user-joined:", emailId);

      if (peers[emailId]) {
        console.log(
          "[ROOM] Already have peer for",
          emailId,
          "- ignoring duplicate user-joined"
        );
        return;
      }

      if (remoteOfferedRef.current[emailId]) {
        console.log(
          "[ROOM] Peer",
          emailId,
          "already offered—skipping createOffer to avoid glare."
        );
        return;
      }

      if (!isAdminObserver) {
        await waitForStream();
      }

      const offer = await createOfferFor(emailId, (to, candidate) => {
        socket.emit("webrtc-candidate", { to, candidate });
      });

      socket.emit("webrtc-offer", { to: emailId, offer });
    },
    [peers, createOfferFor, socket, waitForStream, isAdminObserver]
  );

  const handleOffer = useCallback(
    async ({ from, offer }) => {
      console.log("[ROOM] Received offer from", from);

      remoteOfferedRef.current[from] = true;

      if (!isAdminObserver) {
        await waitForStream();
      }

      const answer = await createAnswerFor(from, offer, (to, candidate) => {
        socket.emit("webrtc-candidate", { to, candidate });
      });

      socket.emit("webrtc-answer", { to: from, answer });
    },
    [createAnswerFor, socket, waitForStream, isAdminObserver]
  );

  const handleAnswer = useCallback(
    async ({ from, answer }) => {
      console.log("[ROOM] Received answer from", from);
      await setRemoteAnswerFor(from, answer);
    },
    [setRemoteAnswerFor]
  );

  const handleCandidate = useCallback(
    async ({ from, candidate }) => {
      console.log("[ROOM] Received candidate from", from);
      if (!candidate) return;
      await addIceCandidateFor(from, candidate);
    },
    [addIceCandidateFor]
  );

  const handleUserLeft = useCallback(
    ({ emailId }) => {
      console.log("[ROOM] user-left:", emailId);
      if (emailId) {
        removePeer(emailId);
        setRemoteMediaState((prev) => {
          const next = { ...prev };
          delete next[emailId];
          return next;
        });
      }
    },
    [removePeer]
  );

  useEffect(() => {
    if (!socket) return;

    socket.onAny((event, ...args) => {
      console.log("[SOCKET EVENT]", event, args);
    });

    socket.on("user-joined", handleNewUser);
    socket.on("webrtc-offer", handleOffer);
    socket.on("webrtc-answer", handleAnswer);
    socket.on("webrtc-candidate", handleCandidate);
    socket.on("user-left", handleUserLeft);

    return () => {
      socket.off("user-joined", handleNewUser);
      socket.off("webrtc-offer", handleOffer);
      socket.off("webrtc-answer", handleAnswer);
      socket.off("webrtc-candidate", handleCandidate);
      socket.off("user-left", handleUserLeft);
      socket.offAny();
    };
  }, [
    socket,
    handleNewUser,
    handleOffer,
    handleAnswer,
    handleCandidate,
    handleUserLeft,
  ]);

  // ====== controls ======
  const toggleMyMic = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) return;
    const newState = !isMicOn;
    audioTracks.forEach((t) => (t.enabled = newState));
    setIsMicOn(newState);
  };

  const toggleMyCam = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const videoTracks = stream.getVideoTracks();
    if (!videoTracks.length) return;
    const newState = !isCamOn;
    videoTracks.forEach((t) => (t.enabled = newState));
    setIsCamOn(newState);
  };

  const toggleRemoteAudio = () => {
    if (!remoteVideoRef.current) return;
    const newState = !isRemoteAudioOn;
    remoteVideoRef.current.muted = !newState;
    setIsRemoteAudioOn(newState);
  };

  const toggleRemoteVideo = () => {
    if (!remoteVideoRef.current) return;
    const newState = !isRemoteVideoOn;
    remoteVideoRef.current.style.visibility = newState ? "visible" : "hidden";
    setIsRemoteVideoOn(newState);
  };

  const toggleRemoteTileAudio = (id) => {
    setRemoteMediaState((prev) => {
      const curr = prev[id] || { audioOn: true, videoOn: true };
      const next = { ...curr, audioOn: !curr.audioOn };
      const el = remoteVideoRefs.current[id];
      if (el) {
        el.muted = !next.audioOn;
      }
      return { ...prev, [id]: next };
    });
  };

  const toggleRemoteTileVideo = (id) => {
    setRemoteMediaState((prev) => {
      const curr = prev[id] || { audioOn: true, videoOn: true };
      const next = { ...curr, videoOn: !curr.videoOn };
      const el = remoteVideoRefs.current[id];
      if (el) {
        el.style.visibility = next.videoOn ? "visible" : "hidden";
      }
      return { ...prev, [id]: next };
    });
  };

  // screen share (only for normal participants)
  const toggleScreenShare = async () => {
    if (!myStream || isAdminObserver) return;

    if (!isScreenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false,
        });

        const screenTrack = screenStream.getVideoTracks()[0];
        screenShareStreamRef.current = screenStream;

        Object.values(peers).forEach((pc) => {
          const sender = pc
            .getSenders()
            .find((s) => s.track && s.track.kind === "video");
          if (sender) {
            if (!cameraVideoTrackRef.current) {
              cameraVideoTrackRef.current = sender.track;
            }
            sender.replaceTrack(screenTrack);
          }
        });

        screenTrack.onended = () => {
          if (isScreenSharing) {
            toggleScreenShare();
          }
        };

        setIsScreenSharing(true);
      } catch (err) {
        console.error("[ROOM] Screen share error:", err);
      }
    } else {
      const camTrack = cameraVideoTrackRef.current;
      if (camTrack) {
        Object.values(peers).forEach((pc) => {
          const sender = pc
            .getSenders()
            .find((s) => s.track && s.track.kind === "video");
          if (sender) {
            sender.replaceTrack(camTrack);
          }
        });
      }

      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach((t) => t.stop());
        screenShareStreamRef.current = null;
      }
      setIsScreenSharing(false);
    }
  };

  const handleExit = useCallback(() => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (socket && roomId && emailIdForServer) {
        socket.emit("leave-room", { roomId, emailId: emailIdForServer });
      }
      resetAllPeers();
    } catch (err) {
      console.error("[ROOM] Error during exit:", err);
    }
    navigate("/");
  }, [socket, roomId, emailIdForServer, resetAllPeers, navigate]);

  // ====== live transcript collection ======
  const appendTranscriptChunk = useCallback(
    (text, fromEmail) => {
      if (!text || !text.trim()) return;

      const label = fromEmail || myEmail || "You";
      const ts = new Date().toISOString();
      const line = `[${ts} ${label}] ${text.trim()}`;

      setTranscriptDraft((prev) => (prev ? prev + "\n" + line : line));

      if (socket && roomId) {
        socket.emit("transcript-chunk", {
          roomId,
          emailId: label,
          text: text.trim(),
          at: ts,
        });
      }
    },
    [socket, roomId, myEmail]
  );

  // Web Speech API wiring
  useEffect(() => {
    if (typeof window === "undefined") return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn("[ASR] Web Speech API not available in this browser.");
      setIsAsrAvailable(false);
      return;
    }

    setIsAsrAvailable(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-IN";

    recognition.onresult = (event) => {
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        }
      }

      if (finalTranscript.trim()) {
        console.log("[ASR] final:", finalTranscript);
        appendTranscriptChunk(finalTranscript, myEmail);
      }
    };

    recognition.onerror = (event) => {
      console.warn("[ASR] error:", event.error, event.message || "");
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        asrShouldRunRef.current = false;
        setIsAsrOn(false);
      }
    };

    recognition.onend = () => {
      console.log("[ASR] ended");
      if (asrShouldRunRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.warn("[ASR] restart failed:", e);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      asrShouldRunRef.current = false;
      try {
        recognition.stop();
      } catch (_) {}
      recognitionRef.current = null;
    };
  }, [appendTranscriptChunk, myEmail]);

  useEffect(() => {
    const recognition = recognitionRef.current;
    if (!recognition || !isAsrAvailable) return;

    if (isAsrOn) {
      asrShouldRunRef.current = true;
      try {
        recognition.start();
        console.log("[ASR] started");
      } catch (e) {
        console.warn("[ASR] start error:", e);
      }
    } else {
      asrShouldRunRef.current = false;
      try {
        recognition.stop();
        console.log("[ASR] stopped");
      } catch (e) {
        console.warn("[ASR] stop error:", e);
      }
    }
  }, [isAsrOn, isAsrAvailable]);

  return (
    <div className="room-page">
      <div className="room-container">
        {/* shared CSS for hover controls & speaker ring */}
        <style>{`
@keyframes speakerGlow {
  0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.65); }
  60% { box-shadow: 0 0 0 10px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}
.speaker-ring {
  animation: speakerGlow 1.1s infinite;
}
@media (hover: hover) {
  .local-surface .local-controls,
  .remote-surface .remote-controls {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
  }
  .local-surface:hover .local-controls,
  .remote-surface:hover .remote-controls {
    opacity: 1;
    pointer-events: auto;
  }
}
        `}</style>

        <RoomHeader
          roomId={roomId}
          myEmail={myEmail}
          connected={connected}
          participantCount={participantCount}
          logo={logo}
          isAdminViewer={isAdminObserver}
        />

        {/* Desktop layout */}
        {!isMobile && mainParticipant && (
          <div className="room-mainLayoutRow">
            <div className="room-mainColumn">
              <MainVideoPanel
                mainParticipant={mainParticipant}
                pinnedId={pinnedId}
                setPinnedId={setPinnedId}
                activeSpeakerId={activeSpeakerId}
                remoteMediaState={remoteMediaState}
                remoteVideoRefs={remoteVideoRefs}
                myStream={myStream}
                isMicOn={isMicOn}
                isCamOn={isCamOn}
                toggleMyMic={toggleMyMic}
                toggleMyCam={toggleMyCam}
                toggleRemoteTileAudio={toggleRemoteTileAudio}
                toggleRemoteTileVideo={toggleRemoteTileVideo}
                isScreenSharing={isScreenSharing}
                toggleScreenShare={toggleScreenShare}
                handleExit={handleExit}
                screenShareStreamRef={screenShareStreamRef}
                // consistent observer props
                isObserver={isAdminObserver}
                isAdminViewer={isAdminObserver}
              />

              <MomPanel
                transcriptDraft={transcriptDraft}
                setTranscriptDraft={setTranscriptDraft}
                mom={mom}
                momLoading={momLoading}
                momError={momError}
                generateMom={generateMom}
                isAsrAvailable={isAsrAvailable}
                isAsrOn={isAsrOn}
                setIsAsrOn={setIsAsrOn}
              />
            </div>

            <div className="room-sidebarColumn">
              <SidebarTiles
                sidebarTiles={sidebarTiles}
                pinnedId={pinnedId}
                setPinnedId={setPinnedId}
                activeSpeakerId={activeSpeakerId}
                myStream={myStream}
                remoteMediaState={remoteMediaState}
                remoteVideoRefs={remoteVideoRefs}
                toggleMyMic={toggleMyMic}
                toggleMyCam={toggleMyCam}
                isMicOn={isMicOn}
                isCamOn={isCamOn}
                toggleRemoteTileAudio={toggleRemoteTileAudio}
                toggleRemoteTileVideo={toggleRemoteTileVideo}
                isObserver={isAdminObserver}
                isAdminViewer={isAdminObserver}
              />
            </div>
          </div>
        )}

        {/* Mobile layout */}
        {isMobile && (
          <MobileLayout
            primaryRemoteEmail={primaryRemoteEmail}
            remoteSurfaceRef={remoteSurfaceRef}
            remoteVideoRef={remoteVideoRef}
            pipPos={pipPos}
            onPipDragStart={onPipDragStart}
            isPipHovered={isPipHovered}
            setIsPipHovered={setIsPipHovered}
            localPipRef={localPipRef}
            myStream={myStream}
            isMicOn={isMicOn}
            isCamOn={isCamOn}
            toggleMyMic={toggleMyMic}
            toggleMyCam={toggleMyCam}
            toggleRemoteAudio={toggleRemoteAudio}
            toggleRemoteVideo={toggleRemoteVideo}
            isRemoteAudioOn={isRemoteAudioOn}
            isRemoteVideoOn={isRemoteVideoOn}
            transcriptDraft={transcriptDraft}
            setTranscriptDraft={setTranscriptDraft}
            mom={mom}
            momLoading={momLoading}
            momError={momError}
            generateMom={generateMom}
            isAdminViewer={isAdminObserver}
          />
        )}

        {/* helper button for debugging transcript push */}
        <button
          type="button"
          onClick={() => appendTranscriptChunk(transcriptDraft, myEmail)}
          style={{ marginTop: 6, fontSize: 11 }}
        >
          Push current draft to server transcript
        </button>

        {/* Bottom call bar – mobile only */}
        {isMobile && (
          <div className="room-callBar">
            <button className="room-endCallButton" onClick={handleExit}>
              <span className="room-endCallIcon">📞</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomPage;
